import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { EnactLoop } from '@components/wee/enact-loop';
import { MotionReveal } from '@components/motion/scroll-motion';
import { useWaterStory } from '@components/water/water-story';
import { colors } from '@config/colors';
import { media } from '@config/breakpoints';
import { spacing } from '@config/spacing';
import { typography } from '@config/typography';

/** The house alone — a window onto the Enact picture around it. */
const HOUSE_WINDOW = '70 44 160 128';
/** The card's window: the house a third of the card's width, as the mock sizes it, with room for the burst. */
const CARD_WINDOW = '-10 -42 320 300';

/** How much of the section must be on screen for the swirl to begin, and to be whole. */
const SWIRL_FROM = 0.2;
const SWIRL_TO = 0.75;

function smoothstep(from: number, to: number, x: number): number {
  const p = Math.max(0, Math.min(1, (x - from) / (to - from)));
  return p * p * (3 - 2 * p);
}

/**
 * The Result: what the story comes to. The page stays in the glow Enact
 * ended in — the water turns to a gentle swirl of the sunrise's colours
 * while this section fills the screen — and the house keeps coming alive.
 */
export const ResultStory: React.FC = () => {
  const story = useWaterStory();
  const rootRef = useRef<HTMLDivElement>(null);

  // Tell the water how much of the section is on screen, as it scrolls.
  useEffect(() => {
    const section = rootRef.current?.closest('section') ?? rootRef.current;
    if (!section) return undefined;
    const shared = story.current;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const box = section.getBoundingClientRect();
      const view = window.innerHeight;
      const visible = Math.min(box.bottom, view) - Math.max(box.top, 0);
      const coverage = Math.max(0, visible) / Math.max(1, Math.min(box.height, view));
      const swirl = smoothstep(SWIRL_FROM, SWIRL_TO, coverage);
      if (swirl !== shared.swirl) {
        shared.swirl = swirl;
        shared.repaint?.();
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    measure();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
      shared.swirl = 0;
      shared.repaint?.();
    };
  }, [story]);

  return (
    <Root ref={rootRef}>
      <MotionReveal>
        <Text>
          <HouseMark aria-hidden="true">
            <EnactLoop viewBox={HOUSE_WINDOW} />
          </HouseMark>
          <Eyebrow>Result</Eyebrow>
          <Heading>
            Your effort for a happy family — <Amplified>amplified.</Amplified>
          </Heading>
          <Body>
            <Short>You still make the calls. The Home CEO does the research, logistics and follow-through.</Short>
            <Long>
              You still make the calls. The Home CEO does the research, the logistics and the follow-through.
            </Long>
          </Body>
          {/* Product review quotes from moms go here (June, 2026-09-23): their
              own words on what the app changed, under the promise above. */}
        </Text>
      </MotionReveal>
      <Card aria-hidden="true">
        <EnactLoop viewBox={CARD_WINDOW} />
      </Card>
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  text-align: center;

  ${media.lg} {
    grid-template-columns: 1fr 1fr;
    align-items: center;
    justify-items: stretch;
    text-align: left;
    gap: ${spacing['5xl']};
  }
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${media.lg} {
    align-items: flex-start;
  }
`;

/* Stacked, the house stands over the words; three across it is the card's. */
const HouseMark = styled.div`
  width: min(200px, 52vw);
  margin-bottom: ${spacing.lg};

  svg {
    display: block;
    width: 100%;
    height: auto;
  }

  ${media.lg} {
    display: none;
  }
`;

/* The mock's gold eyebrow, taken to the warm rung that clears WCAG AA as
   small text on the swirl. */
const Eyebrow = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.accent.orange.exxtraDark};
  margin-bottom: ${spacing.lg};
`;

const Heading = styled.h2`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(2.25rem, 5vw, 4.25rem);
  font-weight: 300;
  line-height: 1.08;
  letter-spacing: -0.02em;
  color: ${colors.neutral.extraDark};
  max-width: 12em;
  margin: 0 0 ${spacing.xl};
`;

/* Gold into coral, the sunrise's own direction; one rung deeper than the
   mock so it clears 3:1 as large text. */
const Amplified = styled.span`
  font-weight: 500;
  background: linear-gradient(90deg, ${colors.accent.yellow.exxtraDark}, ${colors.accent.coral.exxtraDark});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
`;

const Body = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: clamp(1rem, 1.3vw, 1.125rem);
  line-height: 1.6;
  color: ${colors.highlight.primary};
  max-width: 30em;
  margin: 0;
`;

/* The mock words the line a little shorter on a phone. */
const Short = styled.span`
  ${media.sm} {
    display: none;
  }
`;

const Long = styled.span`
  display: none;

  ${media.sm} {
    display: inline;
  }
`;

const Card = styled.div`
  display: none;

  ${media.lg} {
    display: grid;
    place-items: center;
    justify-self: end;
    width: 100%;
    max-width: 520px;
    aspect-ratio: 1;
    overflow: hidden;
    border-radius: 40px;
    background: rgba(255, 251, 245, 0.55);
    border: 1px solid rgba(255, 255, 255, 0.85);
    box-shadow: 0 30px 70px rgba(196, 126, 8, 0.12);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);

    > div {
      width: 86%;
    }

    svg {
      display: block;
      width: 100%;
      height: auto;
    }
  }
`;
